import { useState, useEffect } from 'react';
import { IIrpItemAddEditRequest } from '../../../../../model/scp/irp/request/irp-item-add-edit-request';
import { IIrpItemResponse } from '../../../../../model/scp/irp/response/irp-item-response';
import { ITableResponse } from '../../../../../model/scp/purchase/response/tables-response';
import BaseAppService from '../../../../../application/platform/scp/base/base-app-service';
import IrpAppService from '../../../../../application/platform/scp/irp/irp-app-service';
import { useBaseLayout } from '../../../../../components/context/use-base-layout';
import { sanitizeText } from '../../../../../utils/text';

const ITEMS_PAGE_SIZE = 50;

export const useIrpItemController = (
    irpId: string | undefined,
    modalityId: number | null | undefined,
    setItemsIrpJson: (value: string) => void,
    initialItemsJson?: string
) => {
    const { showDialog, showMessage, showLoading } = useBaseLayout();
    const [itemIrp, setItemIrp] = useState<IIrpItemAddEditRequest>({ applicabilityMarginPreference: false, requirementNationalContent: false, indConfidentialValue: false });
    const [openDrawer, setOpenDrawer] = useState<boolean>(false);
    const [materialService, setMaterialService] = useState<ITableResponse[]>([]);
    const [categoryItemPca, setCategoryItemPca] = useState<ITableResponse[]>([]);
    const [judgementCriteria, setJudgementCriteria] = useState<ITableResponse[]>([]);
    const [titleDrawer, setTitleDrawer] = useState<string>("Novo Item");
    const [isRetification, setIsRetification] = useState<boolean>(false);

    const getInitialItems = (): IIrpItemResponse[] => {
        if (irpId === undefined && initialItemsJson) {
            try {
                return JSON.parse(initialItemsJson);
            } catch (error) {
                showMessage({
                    open: true,
                    message: 'Não foi possível recuperar os itens salvos anteriormente',
                    detail: (error as any).details || (error as Error).message,
                    type: 'warning'
                });
            }
        }
        return [];
    };

    const [irpItems, setIrpItems] = useState<IIrpItemResponse[]>(getInitialItems());
    const [loadMoreData, setLoadMoreData] = useState(false);
    const [page, setPage] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);

    useEffect(() => {
        loadTables();
    }, [modalityId]);

    useEffect(() => {
        loadCategoryItemPca();
    }, []);

    useEffect(() => {
        loadData();
    }, [irpId, page]);

    useEffect(() => {
        if (irpItems !== undefined) {
            setItemsIrpJson(JSON.stringify(irpItems));
        }
    }, [irpItems]);

    const loadTables = async () => {
        const materialServiceList = await BaseAppService.listMaterialService(modalityId);
        setMaterialService(materialServiceList);

        const judgementCriteriaList = await BaseAppService.listJudgmentCriteria(modalityId);
        setJudgementCriteria(judgementCriteriaList);
    };

    const loadCategoryItemPca = async () => {
        const categoryItemPcaList = await BaseAppService.listCategoryItemPca();
        setCategoryItemPca(categoryItemPcaList);
    };

    const loadData = async () => {
        if (irpId !== undefined) {
            setLoadMoreData(true);
            // O endpoint não retorna total de registros: se a página voltar cheia
            // (igual ao pageSize pedido), assume-se que pode haver uma próxima página.
            const fetched = await IrpAppService.listIrpItems({ id: Number(irpId), page: page + 1, pageSize: ITEMS_PAGE_SIZE });
            setIrpItems(fetched);
            setHasNextPage(fetched.length === ITEMS_PAGE_SIZE);
            setLoadMoreData(false);
        }
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const deleteItem = (number: number | undefined, justification?: string) => {
        if (number === undefined) return;

        if (irpId === undefined) {
            const result = irpItems.filter((item) => item.number !== number);
            setIrpItems(result);
        } else {
            (async () => {
                try {
                    showLoading({ open: true });
                    await IrpAppService.deleteItemsIrp(Number(irpId), [number], justification || '');
                    await loadData();
                    showLoading({ open: false });
                    showMessage({ open: true, message: "Item excluído com sucesso!", type: "success" });
                } catch (error) {
                    showLoading({ open: false });
                    showMessage({ open: true, message: "Erro ao excluir item", detail: (error as any).details || (error as Error).message, type: "error" });
                }
            })();
        }
    };

    const handleOnChange = (e: any) => {
        if (e === undefined || e.target === undefined) return;

        const { name, value, checked } = e.target;

        setItemIrp(prevState => {
            switch (name) {
                case "select-type-item":
                    return { ...prevState, typeItem: value };
                case "select-category-item":
                    return { ...prevState, categoryItemId: value };
                case "select-criterion-judgment":
                    return { ...prevState, criterionJudgmentId: value };
                case "input-description":
                    return { ...prevState, description: sanitizeText(value) };
                case "input-complementary-information-item":
                    return { ...prevState, complementaryInformation: sanitizeText(value) };
                case "input-unit-supply":
                    return { ...prevState, unitSupply: value };
                case "check-applicability-margin-preference":
                    return { ...prevState, applicabilityMarginPreference: checked };
                case "check-requirement-national-content":
                    return { ...prevState, requirementNationalContent: checked };
                case "check-ind-confidential-value":
                    return { ...prevState, indConfidentialValue: checked };
                case "input-justification-item":
                    return { ...prevState, justification: sanitizeText(value) };
                default:
                    return prevState;
            }
        });
    };

    const handleCurrencyChange = (value: string | undefined, name: string) => {
        const numericValue = parseFloat(value || '0');
        if (name === 'input-unit-value-estimated') {
            setItemIrp(prevState => ({ ...prevState, unitValueEstimated: numericValue }));
        }
    };

    const addItem = () => {
        setTitleDrawer("Adicionar Item");
        setItemIrp({ applicabilityMarginPreference: false, requirementNationalContent: false, indConfidentialValue: false });
        setIsRetification(false);
        setOpenDrawer(true);
    };

    const editItem = (itemNumber: number | undefined) => {
        const result = irpItems.find((item) => item.number === itemNumber);
        if (result !== undefined) {
            setItemIrp({ ...result, typeItem: result.typeItemId ?? result.typeItem });
            setIsRetification(irpId !== undefined);
            setOpenDrawer(true);
            setTitleDrawer("Editar Item Nº " + result.number);
        } else {
            showMessage({ message: 'Item não encontrado', type: 'error', open: true });
        }
    };

    const validFields = (): boolean => {
        let isValid = true;

        if (itemIrp.unitSupply === undefined || itemIrp.unitSupply.length === 0) {
            isValid = false;
        }
        if (itemIrp.unitValueEstimated === undefined || itemIrp.unitValueEstimated === 0) {
            isValid = false;
        }
        if (itemIrp.description === undefined || itemIrp.description.length === 0) {
            isValid = false;
        }

        if (isRetification) {
            if (itemIrp.justification === undefined || itemIrp.justification.length === 0) {
                isValid = false;
            }
        } else {
            if (itemIrp.typeItem === undefined) {
                isValid = false;
            }
            if (itemIrp.categoryItemId === undefined) {
                isValid = false;
            }
            if (itemIrp.criterionJudgmentId === undefined) {
                isValid = false;
            }
        }

        return isValid;
    };

    const mapToAddFormat = (item: IIrpItemAddEditRequest): IIrpItemAddEditRequest => ({
        typeItem: item.typeItem,
        categoryItemId: item.categoryItemId,
        criterionJudgmentId: item.criterionJudgmentId,
        applicabilityMarginPreference: item.applicabilityMarginPreference || false,
        requirementNationalContent: item.requirementNationalContent || false,
        description: item.description || '',
        unitSupply: item.unitSupply || '',
        unitValueEstimated: item.unitValueEstimated || 0,
        indConfidentialValue: item.indConfidentialValue || false,
        gtinCode: item.gtinCode,
        ncmNbsCode: item.ncmNbsCode,
        ncmNbsDescription: item.ncmNbsDescription,
        catalogId: item.catalogId,
        catalogItemCode: item.catalogItemCode,
    });

    const resolveTypeItemDescription = (typeItem: number | null | undefined): string | undefined => {
        if (typeItem === undefined || typeItem === null) return undefined;
        return materialService.find((element) => Number(element.id) === Number(typeItem))?.name;
    };

    const mapToRetifyFormat = (item: IIrpItemAddEditRequest): IIrpItemAddEditRequest => ({
        number: item.number,
        description: item.description || '',
        complementaryInformation: item.complementaryInformation || '',
        unitSupply: item.unitSupply || '',
        unitValueEstimated: item.unitValueEstimated || 0,
    });

    const saveItem = async () => {
        if (!validFields()) {
            showMessage({ open: true, message: "Preencha todos os campos obrigatórios!", type: "error" });
            return;
        }

        const item = { ...itemIrp };

        if (item.number === undefined) {
            if (irpId === undefined) {
                const nextNumber = irpItems.length > 0 ? Math.max(...irpItems.map((i) => i.number || 0)) + 1 : 1;
                const newItem = { ...mapToAddFormat(item), number: nextNumber, typeItemDescription: resolveTypeItemDescription(item.typeItem) } as IIrpItemResponse;
                setIrpItems([...irpItems, newItem]);
                setOpenDrawer(false);
                showMessage({ open: true, message: "Item adicionado com sucesso!", type: "success" });
            } else {
                showLoading({ open: true });
                try {
                    await IrpAppService.addItemsIrp(Number(irpId), [mapToAddFormat(item)]);
                    showLoading({ open: false });
                    setOpenDrawer(false);
                    await loadData();
                    showMessage({ open: true, message: "Item adicionado com sucesso!", type: "success" });
                } catch (error) {
                    showLoading({ open: false });
                    showMessage({ open: true, message: "Erro ao adicionar item", detail: (error as any).details || (error as Error).message, type: "error" });
                }
            }
        } else {
            if (irpId === undefined) {
                const result = irpItems.map((i) => i.number === item.number ? { ...mapToAddFormat(item), number: item.number, typeItemDescription: resolveTypeItemDescription(item.typeItem) } as IIrpItemResponse : i);
                setIrpItems(result);
                setOpenDrawer(false);
                showMessage({ open: true, message: "Item alterado com sucesso!", type: "success" });
            } else {
                showLoading({ open: true });
                try {
                    await IrpAppService.updateItemsIrp(Number(irpId), [mapToRetifyFormat(item)], item.justification || '');
                    showLoading({ open: false });
                    setOpenDrawer(false);
                    await loadData();
                    showMessage({ open: true, message: "Item retificado com sucesso!", type: "success" });
                } catch (error) {
                    showLoading({ open: false });
                    showMessage({ open: true, message: "Erro ao retificar item", detail: (error as any).details || (error as Error).message, type: "error" });
                }
            }
        }
    };

    return {
        // State
        itemIrp,
        openDrawer,
        setOpenDrawer,
        materialService,
        categoryItemPca,
        judgementCriteria,
        titleDrawer,
        irpItems,
        irpId,
        loadMoreData,
        isRetification,
        page,
        hasNextPage,
        itemsPageSize: ITEMS_PAGE_SIZE,

        // Methods
        handleOnChange,
        handleCurrencyChange,
        handlePageChange,
        addItem,
        editItem,
        saveItem,
        deleteItem,
        showDialog,
        showMessage,
        showLoading,
    };
};
